import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@gokrew/ui';
import { useDocumentTitle } from '@gokrew/ui/hooks';
import { Plus, Search, ChevronRight } from 'lucide-react';
import { finishCrmInbox } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { CrmDashboard } from './CrmDashboard';
import { CrmDeals } from './CrmDeals';
import { CrmCompanies } from './CrmCompanies';
import { CrmContacts } from './CrmContacts';
import { CrmActivities } from './CrmActivityFeed';
import { CrmInbox } from './CrmInbox';
import { useCrmDialogs } from './crm-dialogs';
import { CrmLeads, useLeadCreate } from './CrmLeads';
import { CrmLiveDemos } from './CrmLiveDemos';
import { useLiveDemoCreate } from './crm-live-demo-dialog';
import { CrmButton } from './CrmShared';
import { useCrmLeadsHidden } from './crm-leads-hidden';
import styles from './Crm.module.css';

export const CrmPage = () => {
  const { section = 'dashboard' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useCrmFormat();
  const leadsHidden = useCrmLeadsHidden();
  const client = useQueryClient();
  const [search, setSearch] = useState(params.get('search') || '');
  const [query, setQuery] = useState(search);
  const [oauthError, setOauthError] = useState('');
  const [finishing, setFinishing] = useState(false);
  const handledCode = useRef<string | null>(null);
  const validSection = [
    'dashboard',
    'leads',
    'deals',
    'companies',
    'contacts',
    'activities',
    'inbox',
    'demos',
  ].includes(section)
    ? section
    : 'dashboard';
  // Leads can be hidden for a member: send them to Opportunities.
  useEffect(() => {
    if (leadsHidden && validSection === 'leads')
      navigate('/crm/deals', { replace: true });
  }, [leadsHidden, validSection, navigate]);
  useDocumentTitle(`${t(`crm.workspace.${validSection}`)} | KollabHR CRM`);
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setSearch('');
    setQuery('');
  }, [validSection]);
  useEffect(() => {
    if (params.get('inbox') === 'error') {
      setOauthError(t('crm.workspace.inbox_finish_error'));
      navigate('/crm/inbox', { replace: true });
      return;
    }
    if (params.get('inbox') !== 'finish') return;
    const code = params.get('code');
    if (!code || handledCode.current === code) return;
    handledCode.current = code;
    setFinishing(true);
    void finishCrmInbox(code)
      .then(() => client.invalidateQueries({ queryKey: ['crm', 'inboxes'] }))
      .catch(() => setOauthError(t('crm.workspace.inbox_finish_error')))
      .finally(() => {
        setFinishing(false);
        navigate('/crm/inbox', { replace: true });
      });
  }, [params, navigate, client, t]);
  const { showDeal, newDeal, newCompany, newContact, newActivity } =
    useCrmDialogs();
  const createLead = useLeadCreate();
  const createDemo = useLiveDemoCreate();
  const create = () => {
    if (validSection === 'leads') createLead();
    else if (validSection === 'demos') createDemo();
    else if (validSection === 'companies') newCompany();
    else if (validSection === 'contacts') newContact();
    else if (validSection === 'activities') newActivity();
    else newDeal();
  };
  const createLabel = t(
    validSection === 'leads'
      ? 'crm.leads.add'
      : validSection === 'demos'
        ? 'crm.live_demo.new'
        : validSection === 'companies'
          ? 'crm.add_company'
          : validSection === 'contacts'
            ? 'crm.add_contact'
            : validSection === 'activities'
              ? 'crm.workspace.add_activity'
              : 'crm.workspace.new_deal'
  );
  // Opportunities have no top bar: the search sits in the board toolbar and
  // the create button at the right of the heading, to fit more cards.
  const boardPage = validSection === 'deals';
  return (
    <div className={styles.page}>
      {!boardPage && (
        <header className={styles.topbar}>
          <div className={styles.breadcrumb}>
            <span>{t('crm.workspace.workspace')}</span>
            <ChevronRight />
            <strong>{t(`crm.workspace.${validSection}`)}</strong>
          </div>
          <div className={styles.topbarActions}>
            {!['dashboard', 'inbox'].includes(validSection) && (
              <div className={styles.globalSearch}>
                <Input
                  IconPrefix={Search}
                  aria-label={t(`crm.workspace.search_${validSection}`)}
                  placeholder={t(`crm.workspace.search_${validSection}`)}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            )}
            <CrmButton
              variant="adminPrimary"
              IconPrefix={Plus}
              onClick={create}
            >
              {createLabel}
            </CrmButton>
          </div>
        </header>
      )}
      <div className={styles.pageBody}>
        <div className={styles.pageHeading}>
          <div>
            <p className={styles.eyebrow}>
              {t('crm.workspace.sales_workspace')}
            </p>
            <h1>{t(`crm.workspace.${validSection}`)}</h1>
            <p>{t(`crm.workspace.descriptions.${validSection}`)}</p>
          </div>
          {boardPage && (
            <div>
              <CrmButton
                variant="adminPrimary"
                IconPrefix={Plus}
                onClick={create}
              >
                {createLabel}
              </CrmButton>
            </div>
          )}
        </div>
        {finishing && (
          <p role="status" className={styles.notice}>
            {t('crm.workspace.finishing_connection')}
          </p>
        )}
        {oauthError && (
          <p role="alert" className={styles.errorBox}>
            {oauthError}
          </p>
        )}
        {validSection === 'dashboard' && (
          <CrmDashboard onDeal={showDeal} onCreate={() => newDeal()} />
        )}
        {validSection === 'deals' && (
          <CrmDeals
            search={query}
            filter={search}
            onSearch={setSearch}
            onDeal={showDeal}
            onCreate={newDeal}
          />
        )}
        {validSection === 'leads' && (
          <CrmLeads key={query} search={query} onDeal={showDeal} />
        )}
        {validSection === 'companies' && (
          <CrmCompanies key={query} search={query} />
        )}
        {validSection === 'contacts' && (
          <CrmContacts key={query} search={query} />
        )}
        {validSection === 'activities' && (
          <CrmActivities key={query} search={query} onDeal={showDeal} />
        )}
        {validSection === 'inbox' && <CrmInbox />}
        {validSection === 'demos' && <CrmLiveDemos search={query} />}
      </div>
    </div>
  );
};
